/**
 * Registrar un **mantenimiento** de un sub-activo.
 *
 * Es el equivalente manual de `reset_maintenance_counter`, que el panel anterior
 * sólo ejecutaba al cerrar una tarea: vuelve a poner el contador a cero desde la
 * lectura que se indique, sella la fecha y deja programada la siguiente.
 */

import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { formatNumber } from '@/lib/utils';

import type { SubActivo } from '../sub-activos.types';

/** Fecha de hoy en `AAAA-MM-DD`, que es lo que espera la API. */
function hoy(): string {
  const ahora = new Date();
  const mes = String(ahora.getMonth() + 1).padStart(2, '0');
  const dia = String(ahora.getDate()).padStart(2, '0');

  return `${ahora.getFullYear()}-${mes}-${dia}`;
}

export function SubActivoMantenimiento({
  subActivo,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  subActivo: SubActivo;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: { lectura: number; fecha: string; proximo: string }) => void;
  onCerrar: () => void;
}) {
  const [lectura, setLectura] = useState(String(subActivo.lectura_actual));
  const [fecha, setFecha] = useState(hoy());
  const [proximo, setProximo] = useState('');

  const lecturaNumero = Number(lectura);
  const errorLectura =
    !Number.isFinite(lecturaNumero) || lecturaNumero < 0
      ? 'La lectura debe ser un número igual o mayor que 0.'
      : lecturaNumero > subActivo.lectura_actual
        ? `No puede superar la lectura actual (${formatNumber(subActivo.lectura_actual)}).`
        : null;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title="Registrar mantenimiento"
      description={`${subActivo.nombre} · ${subActivo.codigo}`}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={Boolean(errorLectura)}
            onClick={() => onGuardar({ lectura: lecturaNumero, fecha, proximo })}
          >
            Registrar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <Alert variant="error" title="No se pudo registrar">
            {error}
          </Alert>
        ) : null}

        <Alert variant="info">
          Consumo actual: <strong>{formatNumber(subActivo.usado)}</strong> de{' '}
          <strong>{formatNumber(subActivo.umbral)}</strong> galones ({subActivo.progreso}%). Al
          registrar, el contador vuelve a cero desde la lectura que indiques.
        </Alert>

        <Input
          label="Lectura en el momento del mantenimiento"
          type="number"
          min={0}
          step="0.01"
          value={lectura}
          autoFocus
          error={lectura.length > 0 ? (errorLectura ?? undefined) : undefined}
          hint={`Lectura actual del contador: ${formatNumber(subActivo.lectura_actual)}`}
          onChange={(evento) => setLectura(evento.target.value)}
        />

        <Input
          label="Fecha del mantenimiento"
          type="date"
          value={fecha}
          hint="Si se deja vacía, la API sella la fecha de hoy"
          onChange={(evento) => setFecha(evento.target.value)}
        />

        <Input
          label="Próximo mantenimiento"
          type="date"
          value={proximo}
          hint="Opcional"
          onChange={(evento) => setProximo(evento.target.value)}
        />
      </div>
    </Dialog>
  );
}
